import type {
  ChoiceCondition,
  Character,
  FactionId,
  NarrativeFlags,
} from '@/types';
import { FACCIONES } from '@/types';
import { obtenerSkill } from '@/engine/skills/skillTreeData';
import { obtenerHechizo } from '@/engine/sorcery/spellData';

export interface ConditionContext {
  personaje: Pick<
    Character,
    'stats' | 'skillsDesbloqueadas' | 'hechizosAprendidos' | 'inventario'
  >;
  flags: NarrativeFlags;
  reputacion: Record<FactionId, number>;
}

export function evaluarCondicion(c: ChoiceCondition, ctx: ConditionContext): boolean {
  switch (c.kind) {
    case 'stat':
      return ctx.personaje.stats[c.stat] >= c.minimo;
    case 'skill':
      return ctx.personaje.skillsDesbloqueadas.includes(c.skillId);
    case 'item': {
      const item = ctx.personaje.inventario.find((i) => i.id === c.itemId);
      if (!item) return false;
      const requerido = c.cantidad ?? 1;
      return item.cantidad >= requerido;
    }
    case 'flag': {
      const valorEsperado = c.valor ?? true;
      return (ctx.flags[c.flagId] ?? false) === valorEsperado;
    }
    case 'reputation': {
      const actual = ctx.reputacion[c.facciónId] ?? 0;
      if (c.minimo !== undefined && actual < c.minimo) return false;
      if (c.maximo !== undefined && actual > c.maximo) return false;
      return true;
    }
    case 'spell':
      return ctx.personaje.hechizosAprendidos.includes(c.hechizoId);
  }
}

export function evaluarCondiciones(
  condiciones: ChoiceCondition[] | undefined,
  ctx: ConditionContext,
): boolean {
  if (!condiciones || condiciones.length === 0) return true;
  return condiciones.every((c) => evaluarCondicion(c, ctx));
}

const NOMBRE_STAT: Record<string, string> = {
  vida: 'Vida',
  vidaMax: 'Vida máx.',
  mana: 'Mana',
  manaMax: 'Mana máx.',
  empatia: 'Empatía',
  valentia: 'Valentía',
  astucia: 'Astucia',
  resistencia: 'Resistencia',
  vinculo: 'Vínculo',
};

/**
 * Motivo humano por el cual una condición no se cumple.
 * Resuelve IDs internos a nombres amigables de la skill/hechizo/facción.
 */
export function motivoCondicionNoCumplida(
  c: ChoiceCondition,
  ctx: ConditionContext,
): string | null {
  if (evaluarCondicion(c, ctx)) return null;
  switch (c.kind) {
    case 'stat':
      return `Requiere ${NOMBRE_STAT[c.stat] ?? c.stat} ${c.minimo} (tenés ${ctx.personaje.stats[c.stat]}).`;
    case 'skill': {
      const skill = obtenerSkill(c.skillId);
      return `Requiere la habilidad «${skill?.nombre ?? c.skillId}».`;
    }
    case 'item':
      return `Requiere «${c.itemId}»${c.cantidad && c.cantidad > 1 ? ` (×${c.cantidad})` : ''}.`;
    case 'flag':
      return `El mundo todavía no está listo para esa decisión.`;
    case 'reputation': {
      const nombre = FACCIONES[c.facciónId]?.nombre ?? c.facciónId;
      return `Tu reputación con ${nombre} no alcanza.`;
    }
    case 'spell': {
      const spell = obtenerHechizo(c.hechizoId);
      return `Requiere el hechizo «${spell?.nombre ?? c.hechizoId}».`;
    }
  }
}
